/**
 * Transient IndexedDB failures (browser tears down the IDB server under memory pressure,
 * long backgrounding, etc.). These often recover with a single reload.
 */

import { flattenCaughtMessage } from './errorUtils'

/**
 * Matches browser errors when IndexedDB is temporarily unusable (backing process died,
 * backing store open failed under pressure, etc.). Kept strict so we do not swallow
 * unrelated storage or app errors.
 */
export function isIndexedDbConnectionLostError(reason: unknown): boolean {
  const message = flattenCaughtMessage(reason).toLowerCase()
  if (!message) {
    return false
  }

  if (message.includes('connection to indexed database server lost')) {
    return true
  }

  // Chromium / WebKit sometimes pair this phrase with the standard hint.
  if (message.includes('indexed database server lost') && message.includes('refresh the page')) {
    return true
  }

  // Chromium: DOMException / UnknownError when the IDB backing store cannot open
  // (disk pressure, browser internal teardown). Often paired with "UnknownError:" prefix.
  if (message.includes('internal error opening backing store') && message.includes('indexeddb')) {
    return true
  }

  return false
}

/**
 * True when IndexedDB rejected a transaction because a named object store is missing
 * (corrupt or never-migrated DB schema). Distinct from connection-lost errors.
 */
export function isIndexedDbMissingObjectStoreError(reason: unknown): boolean {
  const message = flattenCaughtMessage(reason).toLowerCase()
  if (!message) {
    return false
  }
  return message.includes('one of the specified object stores was not found')
}

/**
 * True when IndexedDB rejected a write because storage quota was exceeded.
 */
export function isIndexedDbQuotaExceededError(reason: unknown): boolean {
  if (reason instanceof DOMException && reason.name === 'QuotaExceededError') {
    return true
  }
  const message = flattenCaughtMessage(reason).toLowerCase()
  if (!message) {
    return false
  }
  return message.includes('quota') && message.includes('exceeded')
}

/**
 * Errors that may recover after closing and reopening the evidence-cache database.
 */
export function isRecoverableEvidenceCacheError(reason: unknown): boolean {
  return isIndexedDbConnectionLostError(reason) || isIndexedDbMissingObjectStoreError(reason)
}
